import React, { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import * as Sentry from '@sentry/react';
import styles from './DataCoverageStrip.module.scss';

/**
 * DataCoverageStrip
 *
 * Deterministic, always-on data-freshness summary for the IT Report. It answers the
 * Head of IT's signature question — "what is NOT in this report?" — without relying on
 * AI. It polls the lightweight /api/insights/it-data-coverage endpoint (which does not
 * gate on AI being enabled) and shows, per feed, whether data is reporting, missing, or
 * stale (older than the server's threshold, currently 48h).
 *
 * Designed as a handover artifact: an incoming IT lead can glance at this strip and
 * immediately see which telemetry feeds are healthy vs. need attention.
 */
const formatAge = (ageHours) => {
  if (ageHours == null) return 'age unknown';
  if (ageHours < 1) return 'updated < 1h ago';
  if (ageHours < 48) return `updated ${Math.round(ageHours)}h ago`;
  const days = Math.floor(ageHours / 24);
  return `updated ${days}d ago`;
};

const DataCoverageStrip = () => {
  const { apiFetch } = useAuth();
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await apiFetch('/api/insights/it-data-coverage');
        if (!res.ok) throw new Error('Failed to load data coverage');
        const json = await res.json();
        if (active) setCoverage(json);
      } catch (err) {
        Sentry.captureException(err);
        if (active) setError('Could not load data-coverage status.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [apiFetch]);

  if (loading) {
    return (
      <div className={styles.strip} role="status" aria-live="polite">
        <span className={styles.headline}>Checking data coverage…</span>
      </div>
    );
  }

  if (error || !coverage) {
    return (
      <div className={`${styles.strip} ${styles.stripError}`}>
        <span className={styles.headline}>{error || 'Data-coverage status unavailable.'}</span>
      </div>
    );
  }

  const {
    totalFeeds = 0,
    presentFeeds = 0,
    staleFeeds = 0,
    missingFeeds = 0,
    staleThresholdHours = 48,
    feeds = [],
  } = coverage;

  const allHealthy = missingFeeds === 0 && staleFeeds === 0;
  const issues = feeds.filter((f) => !f.present || f.stale);

  const statusClass = allHealthy
    ? styles.ok
    : missingFeeds > 0
      ? styles.bad
      : styles.warn;

  const feedStateClass = (f) => {
    if (!f.present) return styles.bad;
    if (f.stale) return styles.warn;
    return styles.ok;
  };

  const feedStateLabel = (f) => {
    if (!f.present) return 'Missing';
    if (f.stale) return 'Stale';
    return 'Reporting';
  };

  return (
    <div className={`${styles.strip} ${statusClass}`}>
      <div className={styles.summaryRow}>
        <div className={styles.headlineGroup}>
          <span className={styles.dot} aria-hidden="true" />
          <span className={styles.headline}>
            {presentFeeds}/{totalFeeds} data feeds reporting
          </span>
          {staleFeeds > 0 && (
            <span className={styles.badgeWarn}>{staleFeeds} stale (&gt;{staleThresholdHours}h)</span>
          )}
          {missingFeeds > 0 && (
            <span className={styles.badgeBad}>{missingFeeds} missing</span>
          )}
          {allHealthy && (
            <span className={styles.badgeOk}>All feeds healthy</span>
          )}
        </div>
        <button
          type="button"
          className={styles.toggle}
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          {expanded ? 'Hide detail' : 'Show detail'}
        </button>
      </div>

      {!expanded && issues.length > 0 && (
        <div className={styles.issueLine}>
          Needs attention:{' '}
          {issues.map((f, i) => (
            <span key={f.key} className={feedStateClass(f)}>
              {f.name} ({feedStateLabel(f).toLowerCase()}){i < issues.length - 1 ? ', ' : ''}
            </span>
          ))}
        </div>
      )}

      {expanded && (
        <ul className={styles.feedList}>
          {feeds.map((f) => (
            <li key={f.key} className={styles.feedItem}>
              <span className={`${styles.feedStatus} ${feedStateClass(f)}`}>
                {feedStateLabel(f)}
              </span>
              <span className={styles.feedName}>{f.name}</span>
              <span className={styles.feedAge}>
                {f.present ? formatAge(f.ageHours) : 'no data this period'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default DataCoverageStrip;
